import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Screen } from '../components/Screen';
import { AppHeader } from '../components/AppHeader';
import { Spinner } from '../components/Spinner';
import { useAgenda } from '../api/queries';
import { api } from '../api/client';
import { useTenant } from '../auth/TenantProvider';
import { getAccentColor } from '../types/agenda';
import { AccountMenu } from '../auth/AccountMenu';
import './timeline.css';

export function TimelineScreen() {
  const navigate = useNavigate();
  const { activeTenant } = useTenant();
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <Navigate to="/" />;
  }

  const { data: agenda, isLoading: agendaLoading, error: agendaError } = useAgenda(id);

  const {
    data: timelineResult,
    isLoading: computingTimeline,
    error: timelineError,
  } = useQuery({
    queryKey: ['timeline', id],
    enabled: !!agenda,
    queryFn: () => {
      if (!agenda) throw new Error('Agenda not loaded');
      return api.computeTimeline(agenda);
    },
  });

  const isLoading = agendaLoading || computingTimeline;
  const error = agendaError || timelineError;
  const meetingType = agenda?.meetingType;
  const accent = meetingType ? getAccentColor(meetingType, activeTenant) : '#ccc';

  if (isLoading) {
    return (
      <Screen>
        <AppHeader title="Timeline" onBack={() => navigate(`/agenda/${id}/edit`)} accent={accent} actions={<AccountMenu />} />
        <div className="state-center">
          <Spinner />
        </div>
      </Screen>
    );
  }

  if (error || !agenda || !timelineResult) {
    return (
      <Screen>
        <AppHeader title="Timeline" onBack={() => navigate(`/agenda/${id}/edit`)} accent={accent} actions={<AccountMenu />} />
        <div className="state-center">
          <p>{error instanceof Error ? error.message : 'Failed to load timeline'}</p>
        </div>
      </Screen>
    );
  }

  const items = timelineResult.agenda.items || [];
  const totalMinutes = timelineResult.agenda.totalMinutes || 0;
  const issues = timelineResult.issues || [];

  return (
    <Screen>
      <AppHeader title="Timeline" onBack={() => navigate(`/agenda/${id}/edit`)} accent={accent} actions={<AccountMenu />} />

      <div className="timeline-content">
        <div className="timeline-note">
          Shows the last saved version of this agenda.
        </div>

        {/* Timeline items */}
        <div className="timeline-section">
          <div className="timeline-items">
            {items.map((item, idx) => {
              const start = item.timeRange?.start || '—';
              const end = item.timeRange?.end || '—';

              return (
                <div key={idx} className="timeline-item">
                  <div className="timeline-time">
                    <span className="time-start">{start}</span>
                    <span className="time-sep">→</span>
                    <span className="time-end">{end}</span>
                  </div>
                  <div className="timeline-item-title">{item.title}</div>
                </div>
              );
            })}
          </div>

          <div className="timeline-total">
            <strong>Total:</strong> {totalMinutes} minutes
          </div>
        </div>

        {/* Issues section */}
        {issues.length > 0 && (
          <div className="timeline-section issues-section">
            <h2>Issues</h2>
            <div className="issues-list">
              {issues.map((issue, idx) => (
                <div key={idx} className="issue-item">
                  <div className="issue-title">{issue.itemTitle}</div>
                  <div className="issue-message">{issue.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}
